import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { GeographicHeatmap } from '@/components/trends/GeographicHeatmap';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Check, ChevronsUpDown } from 'lucide-react';
import { getStateByAbbreviation } from '@/lib/stateUtils';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface OccupationOption {
  code: string;
  title: string;
}

interface HomePageOccupationMapProps {
  selectedOccupation: OccupationOption;
  onOccupationChange: (occupation: OccupationOption) => void;
}

export const HomePageOccupationMap: React.FC<HomePageOccupationMapProps> = ({
  selectedOccupation,
  onOccupationChange
}) => {
  const navigate = useNavigate();
  const [metric, setMetric] = useState<'salary' | 'employment' | 'growth'>('salary');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Normalize SOC code to handle format variations (dots vs dashes)
  const normalizeSocCode = (code: string): string => {
    if (!code) return code;
    // Remove any whitespace and replace dots with dashes
    return code.trim().replace(/\./g, '-');
  };

  // Fetch geographic data for selected occupation
  const { data: geographicData, isLoading: geoLoading, error: geoError } = useQuery({
    queryKey: ['geographic-occupation-homepage', selectedOccupation.code],
    queryFn: async () => {
      // Normalize SOC code before sending to API
      const normalizedCode = normalizeSocCode(selectedOccupation.code);
      // No year: the edge function resolves the latest available OEWS year,
      // so the map advances automatically with each annual data refresh.
      const { data, error } = await supabase.functions.invoke('api-geographic-occupation', {
        body: {
          occupation_codes: [normalizedCode]
        }
      });
      
      if (error) throw error;
      return data?.states || [];
    },
    enabled: !!selectedOccupation.code,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Fetch occupation search results
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['occupations-search-homepage', searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim() || searchQuery.length < 2) return [];
      
      const { data, error } = await supabase.functions.invoke('api-occupations', {
        body: { 
          search: searchQuery,
          limit: 10
        }
      });
      
      if (error) throw error;
      
      // Deduplicate by soc_code
      const seen = new Set<string>();
      return (data?.occupations || []).filter((occ: unknown) => {
        const code = occ.soc_code || occ.occupation_code;
        if (seen.has(code)) return false;
        seen.add(code);
        return true;
      }).slice(0, 10);
    },
    enabled: searchQuery.length >= 2,
  });

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Transform API data to GeographicHeatmap format
  const transformedData = useMemo(() => {
    if (!geographicData || geographicData.length === 0) return [];

    return geographicData.map((state: unknown) => ({
      area_code: state.area_code,
      area_name: state.area_name,
      state_abbr: state.state_abbr,
      a_median: state.a_median || 0,
      employment: state.employment || 0,
      // Keep null (no history) distinct from 0 (flat growth)
      growth_5yr: state.growth_5yr ?? null,
    })).filter((item: unknown) => {
      // Filter out items with invalid state abbreviations
      if (!item.state_abbr || item.state_abbr.length !== 2) {
        return false;
      }
      // For the current metric, ensure value is > 0
      if (metric === 'salary' && item.a_median <= 0) return false;
      if (metric === 'employment' && item.employment <= 0) return false;
      // Growth can be negative, but hide states with no growth history
      if (metric === 'growth') return item.growth_5yr !== null;
      return true;
    });
  }, [geographicData, metric]);

  const handleStateClick = (stateAbbr: string) => {
    const state = getStateByAbbreviation(stateAbbr);
    if (state) {
      navigate(`/state/${state.slug}`);
    }
  };

  const handleOccupationSelect = (occupation: unknown) => {
    const code = occupation.soc_code || occupation.occupation_code;
    const title = occupation.occ_title || occupation.title;
    onOccupationChange({ code, title });
    setIsSearchOpen(false);
    setSearchQuery('');
  };

  const popularOccupations = [
    { code: '15-1252', title: 'Software Developers' },
    { code: '15-2051', title: 'Data Scientists' },
    { code: '29-1171', title: 'Nurse Practitioners' },
    { code: '11-3031', title: 'Financial Managers' },
    { code: '11-2021', title: 'Marketing Managers' },
  ];

  if (geoLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Geographic Salary Analysis</CardTitle>
          <CardDescription>Loading interactive map...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-96 bg-muted/20 rounded-lg animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  if (geoError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Geographic Salary Analysis</CardTitle>
          <CardDescription>Unable to load geographic data. Please try again.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4">
          <div>
            <CardTitle className="text-xl">Geographic Salary Analysis</CardTitle>
            <CardDescription className="mt-2">
              Explore salary data across all 50 states for any occupation. Select an occupation below to see state-by-state comparisons.
            </CardDescription>
          </div>
          
          {/* Occupation Selector */}
          <div className="flex flex-col gap-3">
            <div className="flex-1 min-w-0" ref={searchRef}>
              <Popover open={isSearchOpen} onOpenChange={setIsSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                  >
                    <span className="truncate">
                      {selectedOccupation.title || 'Select occupation...'}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] max-w-[600px] p-0" align="start">
                  {/* shouldFilter=false: results come pre-filtered from the API and may
                      match via aliases (e.g. "doctor" -> physician titles), so cmdk's
                      client-side title filter would wrongly hide them. */}
                  <Command shouldFilter={false}>
                    <CommandInput 
                      placeholder="Search occupations..."
                      value={searchQuery}
                      onValueChange={setSearchQuery}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {isSearching ? 'Searching...' : searchQuery.length < 2 ? 'Type at least 2 characters...' : 'No occupations found.'}
                      </CommandEmpty>
                      <CommandGroup>
                        {searchResults?.map((occupation: unknown) => {
                          const code = occupation.soc_code || occupation.occupation_code;
                          const title = occupation.occ_title || occupation.title;
                          const isSelected = code === selectedOccupation.code;
                          
                          return (
                            <CommandItem
                              key={code}
                              value={title}
                              onSelect={() => handleOccupationSelect(occupation)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  isSelected ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex-1">
                                <div className="font-medium">{title}</div>
                                <div className="text-xs text-muted-foreground">SOC: {code}</div>
                              </div>
                              {occupation.median_salary && (
                                <Badge variant="outline" className="ml-2">
                                  ${(occupation.median_salary / 1000).toFixed(0)}k
                                </Badge>
                              )}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Popular Occupations Quick Select - Underneath search bar horizontally */}
            <div className="flex flex-wrap gap-2">
              {popularOccupations.map((occ) => (
                <Button
                  key={occ.code}
                  variant={selectedOccupation.code === occ.code ? "default" : "outline"}
                  size="sm"
                  onClick={() => onOccupationChange(occ)}
                  className="text-xs whitespace-nowrap"
                >
                  {occ.title}
                </Button>
              ))}
            </div>
          </div>

          {/* Metric Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">View:</span>
            <Select value={metric} onValueChange={(value: 'salary' | 'employment' | 'growth') => setMetric(value)}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="salary">Median Salary</SelectItem>
                <SelectItem value="employment">Employment</SelectItem>
                <SelectItem value="growth">Growth Rate</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {transformedData.length > 0 && transformedData.length < 10 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
              <p className="font-medium">
                Limited state coverage: BLS publishes state-level wage data for only{' '}
                {transformedData.length} {transformedData.length === 1 ? 'state' : 'states'} for this occupation.
              </p>
              <p className="mt-1">
                Wages in other states are suppressed for confidentiality or top-coded by the
                Bureau of Labor Statistics, so they can't be shown on the map.
              </p>
            </div>
          )}
          {transformedData.length > 0 ? (
            <GeographicHeatmap
              data={transformedData}
              metric={metric}
              onMetricChange={setMetric}
              title={`${selectedOccupation.title} by State`}
              occupationTitle={selectedOccupation.title}
              onStateClick={handleStateClick}
              embedded
              showControls={false}
            />
          ) : (
            <div className="h-96 bg-muted/20 rounded-lg flex items-center justify-center">
              <div className="text-center space-y-2 max-w-md px-4">
                <p className="text-muted-foreground">No state-level wage data is published by BLS for this occupation.</p>
                <p className="text-sm text-muted-foreground">
                  Wage estimates may be suppressed for confidentiality or top-coded.
                  Try selecting a different occupation.
                </p>
              </div>
            </div>
          )}
          
          {transformedData.length > 0 && (
            <div className="text-sm text-muted-foreground pt-2 border-t">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
                <div className="text-center sm:text-left">
                  <p>
                    Showing {metric === 'salary' ? 'median salary' : metric === 'employment' ? 'employment' : 'growth rate'} 
                    {' '}for {selectedOccupation.title} across {transformedData.length} states.
                  </p>
                  <p className="mt-1">
                    Click any state on the map to explore detailed wage data and career opportunities.
                  </p>
                </div>
                {/* SOC Code - Right side at bottom */}
                {selectedOccupation.code && (
                  <div className="flex items-center gap-2 text-sm shrink-0">
                    <Badge variant="secondary">SOC: {selectedOccupation.code}</Badge>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
